import React, { useCallback, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useQuery } from 'react-query';
import { useTranslation } from 'react-i18next';

import { Box, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Card, CardContent, Divider, Typography } from '@mui/material';
import Paper from '@mui/material/Paper';

import {
  acceptPolicyVersion,
  getPolicyVersionUserById,
  markPolicyVersionAsRead,
  regenerateAttachmentDownloadUrl,
} from 'services/policies-service';
import { formatDateTimeToDateTimeBR } from 'utils/date_utils';

import { PageLoaderAcceptPolicy } from 'Components/PageLoader/AcceptPolicy';
import { ButtonForm } from 'Components/Form/styles';
import { Footer } from 'Components/Footer';

import TaskAltOutlinedIcon from '@mui/icons-material/TaskAltOutlined';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import DescriptionIcon from '@mui/icons-material/Description';

import withReactContent from 'sweetalert2-react-content';
import Swal from 'sweetalert2';

import 'react-quill/dist/quill.snow.css';

export const AcceptPolicyVersion = () => {
  const { idPolicyVersionUser } = useParams();
  const SwalReact = withReactContent(Swal);
  const [refetch, setRefetch] = useState(false);
  const { t, i18n } = useTranslation(['policy']);

  // Função para determinar o ícone do arquivo baseado no tipo
  const getFileIcon = (mimetype) => {
    if (mimetype === 'application/pdf') {
      return <PictureAsPdfIcon sx={{ color: '#d32f2f', fontSize: 24 }} />;
    } else if (mimetype === 'application/msword' || mimetype === 'application/vnd.openxmlformats-officedocument.wordprocessingml.document') {
      return <DescriptionIcon sx={{ color: '#1976d2', fontSize: 24 }} />;
    }
    return <AttachFileIcon sx={{ color: '#666', fontSize: 24 }} />;
  };

  // Função para formatar o tamanho do arquivo
  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  // Componente para exibir o anexo
  const AttachmentViewer = React.memo(({ attachment }) => {
    const [attachmentUrl, setAttachmentUrl] = useState(null);
    const [isRegenerating, setIsRegenerating] = useState(false);
    const [iframeError, setIframeError] = useState(false);
    const iframeRef = React.useRef(null);

    // Inicializar com URL original
    React.useEffect(() => {
      const init = async () => {
        if (!attachment) return;

        try {
          const response = await regenerateAttachmentDownloadUrl(policyVersionUser.policyId, policyVersionUser.policyVersionId);
          if (response.data?.url) {
            setAttachmentUrl(response.data.url);
          } else {
            console.error("❌ Backend não retornou URL válida");
          }
        } catch (err) {
          setAttachmentUrl(attachment.url);
        }
      };

      init();
    }, [attachment]);

    if (!attachment) return null;

    // Função para regenerar URL automaticamente (chamada pelo onError do iframe)
    const regenerateUrlAutomatically = async () => {
      try {
        setIsRegenerating(true);
        
        const response = await regenerateAttachmentDownloadUrl(policyVersionUser.policyId, policyVersionUser.policyVersionId);
        
        if (response.data?.url) {
          setAttachmentUrl(response.data.url);
          setIframeError(false);
        } else {
          setIframeError(true);
        }
      } catch (error) {
        setIframeError(true);
      } finally {
        setIsRegenerating(false);
      }
    };

    // Função para regenerar URL manualmente
    const regenerateUrl = async () => {
      try {
        setIsRegenerating(true);
        const response = await regenerateAttachmentDownloadUrl(policyVersionUser.policyId, policyVersionUser.policyVersionId);
        if (response.data?.url) {
          setAttachmentUrl(response.data.url);
          setIframeError(false);
          SwalReact.fire({
            title: t('policy:versionShow.swal.successTitle'),
            text: response.data.message || t('policy:versionShow.swal.urlRegenSuccess'),
            icon: 'success',
          });
        }
      } catch (error) {
        SwalReact.fire({
          title: t('policy:versionShow.swal.errorTitle'),
          text: t('policy:versionShow.swal.urlRegenError'),
          icon: 'error',
        });
      } finally {
        setIsRegenerating(false);
      }
    };

    // Função chamada quando o iframe carrega
    const handleIframeLoad = () => {
      setIframeError(false);
      
      // Verificar se o conteúdo do iframe indica erro de acesso
      setTimeout(() => {
        try {
          const iframe = iframeRef.current;
          if (iframe && iframe.contentDocument) {
            const content = iframe.contentDocument.body?.innerText || '';
            
            if (content.includes('Access Denied') || 
                content.includes('AccessDenied') || 
                content.includes('Forbidden') ||
                content.includes('403') ||
                content.includes('Token has expired') ||
                content.includes('SignatureDoesNotMatch') ||
                content.includes('ExpiredToken') ||
                content.includes('The provided token has expired')) {
              setIframeError(true);
              regenerateUrlAutomatically();
            }
          }
        } catch (error) {
          console.log('Não foi possível verificar conteúdo do iframe (CORS):', error.message);
        }
      }, 1000); // Aguardar 1 segundo para o conteúdo carregar
    };

    // Função chamada quando o iframe falha ao carregar
    const handleIframeError = () => {
      setIframeError(true);
      regenerateUrlAutomatically();
    };

    return (
      <Card sx={{ mb: 2 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <AttachFileIcon />
            {t('policy:versionShow.attachment.title')}
          </Typography>
          <Divider sx={{ mb: 2 }} />
          
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
            {getFileIcon(attachment.mimetype)}
            <Box>
              <Typography variant="body1" sx={{ fontWeight: 'medium' }}>
                {attachment.originalName}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {attachment.mimetype} • {formatFileSize(attachment.size)}
              </Typography>
            </Box>
          </Box>

          {/* Visualizador de PDF normal */}
          {attachment.mimetype === 'application/pdf' && (
            <Box sx={{ mt: 2 }}>
              <Typography variant="subtitle2" gutterBottom>
                {t('policy:versionShow.attachment.pdfPreview')}
              </Typography>

              {/* Botão para regenerar URL se houver erro */}
              {iframeError && (
                <Box sx={{ mb: 2, p: 2, backgroundColor: '#fff3cd', borderRadius: 1, border: '1px solid #ffeaa7' }}>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                    {t('policy:versionShow.attachment.linkExpired')}
                  </Typography>
                  <ButtonForm
                    secondary
                    size="small"
                    label={isRegenerating ? t('policy:versionShow.attachment.reloading') : t('policy:versionShow.attachment.reloadDocument')}
                    onClick={regenerateUrl}
                    disabled={isRegenerating}
                  />
                </Box>
              )}
              
              <Box 
                sx={{ 
                  border: '1px solid #ddd', 
                  borderRadius: 1,
                  overflow: 'hidden',
                  height: '600px',
                  width: '100%'
                }}
              >
                <iframe
                  ref={iframeRef}
                  key={attachmentUrl} // Força re-render quando URL muda
                  src={attachmentUrl || 'about:blank'}
                  width="100%"
                  height="100%"
                  style={{ border: 'none' }}
                  title={attachment.originalName}
                  onLoad={handleIframeLoad}
                  onError={handleIframeError}
                >
                  <p>
                    {t('policy:versionShow.attachment.iframeFallback')}
                    <a href={attachmentUrl || '#'} target="_blank" rel="noopener noreferrer">
                      {t('policy:versionShow.attachment.iframeFallbackLink')}
                    </a>
                  </p>
                </iframe>
              </Box>
            </Box>
          )}

          {/* Mensagem para outros tipos de arquivo */}
          {attachment.mimetype !== 'application/pdf' && (
            <Box sx={{ mt: 2, p: 2, backgroundColor: '#f5f5f5', borderRadius: 1 }}>
              <Typography variant="body2" color="text.secondary">
                {t('policy:versionShow.attachment.noPreview')}
              </Typography>
            </Box>
          )}
        </CardContent>
      </Card>
    );
  });

  AttachmentViewer.displayName = 'AttachmentViewer';

  const {
    isLoading,
    error,
    isSuccess,
    data: policyVersionUser,
  } = useQuery(['getPolicyVersionUserById', refetch], async () => {
    const resPolicyVersionUser = await getPolicyVersionUserById(idPolicyVersionUser);

    if (resPolicyVersionUser.status === 200) return resPolicyVersionUser.data;

    return null;
  });

  // Obter a versão da política diretamente dos dados já carregados
  const policyVersion = policyVersionUser?.policy?.policyVersions?.[policyVersionUser?.policyVersionId];

  const markAsReadPolicyVersion = useCallback(async () => {
    try {
      if (!idPolicyVersionUser) return;
      if (!policyVersionUser) return;
      await markPolicyVersionAsRead(idPolicyVersionUser);
    } catch (err) {
      SwalReact.fire({
        title: t('policy:versionShow.swal.errorTitle'),
        text: err.response.data.message ?? err.message,
        icon: 'error',
      });
    }
  }, [idPolicyVersionUser, policyVersionUser, SwalReact, t]);

  const acceptPolicyVersionAction = useCallback(async () => {
    try {
      await acceptPolicyVersion(idPolicyVersionUser);
      setRefetch(true);
      SwalReact.fire({
        title: t('policy:versionShow.swal.successTitle'),
        text: t('policy:acceptPolicy.swal.acceptSuccess'),
        icon: 'success',
      });
    } catch (err) {
      SwalReact.fire({
        title: t('policy:versionShow.swal.errorTitle'),
        text: err.response.data.message ?? err.message,
        icon: 'error',
      });
    }
  }, [idPolicyVersionUser, SwalReact, t]);

  useEffect(() => {
    if (!idPolicyVersionUser) return;
    if (!policyVersionUser) return;
    if (error) return;
    if (!isSuccess) return;
    if (policyVersionUser.readed === true) return;

    markAsReadPolicyVersion();
  }, [idPolicyVersionUser, policyVersionUser, error, isSuccess, markAsReadPolicyVersion]);

  useEffect(() => {
    const lang = policyVersionUser?.user?.preferredLanguage;
    if (lang && i18n.language !== lang) {
      i18n.changeLanguage(lang);
    }
  }, [policyVersionUser, i18n]);

  const getStatusReview = () => {
    return (
      <div style={{ verticalAlign: 'middle', display: 'flex' }}>
        <TaskAltOutlinedIcon sx={{ fill: 'green' }} />
        &nbsp;{t('policy:versionShow.statusReviewed')}
      </div>
    );
  };

  const getStatusApprove = () => {
    return (
      <div style={{ verticalAlign: 'middle', display: 'flex' }}>
        <TaskAltOutlinedIcon sx={{ fill: 'green' }} />
        &nbsp;{t('policy:versionShow.statusApproved')}
      </div>
    );
  };

  const getStatusPublish = () => {
    return (
      <div style={{ verticalAlign: 'middle', display: 'flex' }}>
        <TaskAltOutlinedIcon sx={{ fill: 'green' }} />
        &nbsp;{t('policy:versionShow.statusPublished')}
      </div>
    );
  };

  const getFormAcceptPolicyVersion = () => {
    return (
      <>
        <Box
          sx={{
            width: '100%',
            paddingInline: '16px',
            paddingBottom: '60px', // Espaço para o footer fixo
            fontFamily:
              '"Source Sans Pro", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif, "Apple Color Emoji", "Segoe UI Emoji", "Segoe UI Symbol"',
          }}
          className="container"
        >
          <div>
            <h3
              style={{
                textAlign: 'center',
                fontSize: '1.75rem',
                fontWeight: '500',
                marginTop: '1.5rem',
                marginBottom: '0.5rem',
              }}
            >
              {policyVersionUser?.policyName} ({policyVersionUser?.policyVersion})
            </h3>
          </div>
          <div
            style={{
              textAlign: 'right',
              color: '#6c757d',
              fontSize: '1rem',
              fontWeight: '400',
              marginTop: '0rem',
              marginBottom: '1rem',
            }}
          >
            {policyVersionUser?.policyPublishedDate
              ? formatDateTimeToDateTimeBR(policyVersionUser.policyPublishedDate)
              : t('policy:versionShow.notPublished')}
          </div>
          <div
            style={{ display: 'flex', justifyContent: 'start', flexDirection: 'column' }}
            className="ql-snow ql-editor"
            dangerouslySetInnerHTML={{ __html: policyVersionUser?.policyVersionText }}
          ></div>
           {policyVersion?.attachment && (
            <AttachmentViewer attachment={policyVersion.attachment} />
          )}
          {policyVersion && (
            <TableContainer
              component={Paper}
              style={{
                marginBottom: '1rem',
              }}
            >
              <Table sx={{ minWidth: 650 }} aria-label="simple table">
                <TableHead>
                  <TableRow key={'table-row-header'}>
                    <TableCell key={`thrc-stage`}>{t('policy:versionShow.table.stage')}</TableCell>
                    <TableCell key={`thrc-status`}>{t('policy:versionShow.table.status')}</TableCell>
                    <TableCell key={`thrc-date`}>{t('policy:versionShow.table.date')}</TableCell>
                    <TableCell key={`thrc-sponsor`}>{t('policy:versionShow.table.responsible')}</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  <TableRow key={'table-row-header-review'}>
                    <TableCell key={`thrc-stage-1`}>{t('policy:versionShow.stage.review')}</TableCell>
                    <TableCell key={`thrc-status-1`}>{getStatusReview()}</TableCell>
                    <TableCell key={`thrc-date-1`}>{formatDateTimeToDateTimeBR(policyVersion?.reviewedDate)}</TableCell>
                    <TableCell key={`thrc-sponsor-1`}>{policyVersion?.reviewer?.name}</TableCell>
                  </TableRow>
                  <TableRow key={'table-row-header-approve'}>
                    <TableCell key={`thrc-stage-2`}>{t('policy:versionShow.stage.approval')}</TableCell>
                    <TableCell key={`thrc-status-2`}>{getStatusApprove()}</TableCell>
                    <TableCell key={`thrc-date-2`}>{formatDateTimeToDateTimeBR(policyVersion?.approvedDate)}</TableCell>
                    <TableCell key={`thrc-sponsor-2`}>{policyVersion?.approver?.name}</TableCell>
                  </TableRow>
                  <TableRow key={'table-row-header-publish'}>
                    <TableCell key={`thrc-stage-3`}>{t('policy:versionShow.stage.publication')}</TableCell>
                    <TableCell key={`thrc-status-3`}>{getStatusPublish()}</TableCell>
                    <TableCell key={`thrc-date-3`}>
                      {policyVersion?.publishedDate ? formatDateTimeToDateTimeBR(policyVersion.publishedDate) : t('policy:versionShow.notPublished')}
                    </TableCell>
                    <TableCell key={`thrc-sponsor-3`}>{policyVersion?.author?.name}</TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </TableContainer>
          )}
          {!policyVersionUser?.accepted && <ButtonForm label={t('policy:acceptPolicy.button.accept')} onClick={() => acceptPolicyVersionAction()} />}
          {policyVersionUser?.accepted && (
            <div>
              {t('policy:acceptPolicy.alreadyAccepted', { date: formatDateTimeToDateTimeBR(policyVersionUser?.acceptedDate) })}
            </div>
          )}
        </Box>
      </>
    );
  };

  return (
    <>
      {!isLoading && !error && getFormAcceptPolicyVersion()}
      {isLoading && <PageLoaderAcceptPolicy />}
      {error && <div>{t('policy:page.error')}</div>}
      <Footer />
    </>
  );
};
